import { useState } from 'react';
import { Badge, Button, Card } from '@/components/ui';
import { Field, inputClass } from '@/components/forms/form';
import ServiceIcon, {
  BookingTypeIcon,
  ICON_KEYS,
  categoryTint,
} from '@/components/companion/ServiceIcon';
import { BOOKING_TYPES, type BookingType } from '@makemypartner/shared';
import { bookingTypeLabel } from '@/lib/data/services';
import {
  useAdminCategories,
  createCategory,
  updateCategory,
  slugify,
  type AdminCategory,
  type CategoryDraft,
} from '@/lib/data/catalogue';

/**
 * Where the service catalogue is managed — project.md §10, §74.
 *
 * Until now these ten rows existed only because a migration inserted them, so
 * adding an eleventh meant a developer, a migration and a deploy. Same argument
 * as platform settings: a product decision should not need an engineer.
 *
 * Three things this screen is deliberate about:
 *
 *   · A new service is created INACTIVE. Writing the copy, picking an icon and
 *     choosing the channels are separate decisions, and a half-finished
 *     category should not be on the public browse page in between.
 *
 *   · The slug is shown while creating and frozen afterwards. It is in every
 *     /services/:slug URL, so renaming one 404s links already in the world.
 *     A trigger in migration 0020 enforces this regardless of what the UI does.
 *
 *   · Deactivate, never delete. The count of companions offering a service is
 *     on every row, because that is the difference between a free change and
 *     pulling the rug out from under forty people.
 */

const EMPTY: CategoryDraft = {
  slug: '',
  name: '',
  description: '',
  guidance: '',
  allowedBookingTypes: ['chat'],
  iconKey: 'speech-bubble',
  hue: 210,
  requiresBackgroundCheck: false,
  sortOrder: 0,
};

export default function AdminServices() {
  const { categories, loading, error, reload } = useAdminCategories();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const active = categories.filter((c) => c.is_active);
  const hidden = categories.filter((c) => !c.is_active);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <header className="animate-rise mb-8">
        <p className="eyebrow mb-2">Admin</p>
        <h1 className="text-3xl font-semibold">Services</h1>
        <p className="mt-2 text-[var(--ink-2)]">
          What companions can offer and users can browse. A new service stays hidden until you
          switch it on.
        </p>
      </header>

      {error && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        </Card>
      )}

      {notice && (
        <Card className="mb-6 px-5 py-4">
          <p className="text-sm text-[var(--ink-2)]">{notice}</p>
        </Card>
      )}

      {!creating && (
        <div className="mb-6">
          <Button onClick={() => setCreating(true)}>Add a service</Button>
        </div>
      )}

      {creating && (
        <CategoryForm
          initial={EMPTY}
          showSlug
          submitLabel="Create"
          onCancel={() => setCreating(false)}
          onSubmit={async (d) => {
            const created = await createCategory(d);
            setCreating(false);
            setNotice(`"${created.name}" created, and hidden until you switch it on.`);
            await reload();
          }}
        />
      )}

      {loading ? (
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      ) : (
        <div className="flex flex-col gap-10">
          <Group
            title="Live"
            note="Visible on /services and offerable by companions."
            categories={active}
            editingId={editingId}
            setEditingId={setEditingId}
            onChanged={reload}
            setNotice={setNotice}
          />
          <Group
            title="Hidden"
            note="Not shown to anyone. Existing listings keep working."
            categories={hidden}
            editingId={editingId}
            setEditingId={setEditingId}
            onChanged={reload}
            setNotice={setNotice}
          />
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  note,
  categories,
  editingId,
  setEditingId,
  onChanged,
  setNotice,
}: {
  title: string;
  note: string;
  categories: AdminCategory[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  onChanged: () => Promise<void>;
  setNotice: (m: string | null) => void;
}) {
  if (categories.length === 0) return null;

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-[var(--ink-2)]">{note}</p>
      </div>

      <ul className="flex flex-col gap-3">
        {categories.map((c) => (
          <CategoryRow
            key={c.id}
            category={c}
            editing={editingId === c.id}
            setEditing={(on) => setEditingId(on ? c.id : null)}
            onChanged={onChanged}
            setNotice={setNotice}
          />
        ))}
      </ul>
    </section>
  );
}

function CategoryRow({
  category,
  editing,
  setEditing,
  onChanged,
  setNotice,
}: {
  category: AdminCategory;
  editing: boolean;
  setEditing: (on: boolean) => void;
  onChanged: () => Promise<void>;
  setNotice: (m: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const tint = categoryTint(category.hue);
  const plural = category.listing_count === 1 ? '' : 's';

  async function toggleActive() {
    setBusy(true);
    try {
      await updateCategory(category.id, { isActive: !category.is_active });
      setNotice(
        category.is_active
          ? `"${category.name}" hidden. ${category.listing_count} existing listing${plural} kept.`
          : `"${category.name}" is live.`,
      );
      await onChanged();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Could not change that.');
    }
    setBusy(false);
  }

  return (
    <Card as="li" className="overflow-hidden">
      <div className="flex items-center gap-4 p-4">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          style={tint}
        >
          <ServiceIcon iconKey={category.icon_key} size={22} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{category.name}</h3>
            {category.requires_background_check && <Badge tone="marigold">Background check</Badge>}
            {category.listing_count > 0 && (
              <Badge tone="teal">
                {category.listing_count} companion{plural}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 font-mono text-xs text-[var(--muted)]">/services/{category.slug}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
            {editing ? 'Close' : 'Edit'}
          </Button>
          <Button variant="secondary" size="sm" onClick={toggleActive} disabled={busy}>
            {category.is_active ? 'Hide' : 'Go live'}
          </Button>
        </div>
      </div>

      {editing && (
        <div className="border-t px-4 py-5" style={{ borderColor: 'var(--line)' }}>
          <CategoryForm
            initial={{
              slug: category.slug,
              name: category.name,
              description: category.description ?? '',
              guidance: category.guidance ?? '',
              allowedBookingTypes: category.allowed_booking_types,
              iconKey: category.icon_key,
              hue: category.hue,
              requiresBackgroundCheck: category.requires_background_check,
              sortOrder: category.sort_order,
            }}
            showSlug={false}
            submitLabel="Save"
            onCancel={() => setEditing(false)}
            onSubmit={async (d) => {
              await updateCategory(category.id, {
                name: d.name,
                description: d.description,
                guidance: d.guidance,
                allowedBookingTypes: d.allowedBookingTypes,
                iconKey: d.iconKey,
                hue: d.hue,
                requiresBackgroundCheck: d.requiresBackgroundCheck,
                sortOrder: d.sortOrder,
              });
              setEditing(false);
              setNotice(`"${d.name}" saved.`);
              await onChanged();
            }}
          />
        </div>
      )}
    </Card>
  );
}

function CategoryForm({
  initial,
  showSlug,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: CategoryDraft;
  showSlug: boolean;
  submitLabel: string;
  onSubmit: (draft: CategoryDraft) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<CategoryDraft>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once the admin edits the slug by hand, typing the name stops overwriting it.
  const [slugTouched, setSlugTouched] = useState(!showSlug);

  function set<K extends keyof CategoryDraft>(key: K, value: CategoryDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function toggleBookingType(t: BookingType) {
    const on = draft.allowedBookingTypes.includes(t);
    // Never down to zero — the database rejects it, and a service nobody can
    // book is not a service. Catching it here keeps the error out of the UI.
    if (on && draft.allowedBookingTypes.length === 1) return;

    const next = on
      ? draft.allowedBookingTypes.filter((x) => x !== t)
      : [...draft.allowedBookingTypes, t];

    // Keep them in channel order so the chips do not reshuffle as you click.
    next.sort((a, b) => BOOKING_TYPES.indexOf(a) - BOOKING_TYPES.indexOf(b));
    set('allowedBookingTypes', next);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  const tint = categoryTint(draft.hue);

  return (
    <Card className="mb-6 p-5">
      <div className="flex flex-col gap-5">
        {/* The card exactly as a user will see it on /services. Choosing an
            icon and a colour blind, then loading the browse page to check, is
            how you end up with two services the same shade of purple. */}
        <div>
          <p className="mb-2 text-xs font-medium text-[var(--muted)]">Preview</p>
          <div
            className="flex items-start gap-3 rounded-xl p-4"
            style={{ background: 'var(--surface-2)' }}
          >
            <span
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
              style={tint}
            >
              <ServiceIcon iconKey={draft.iconKey} size={22} />
            </span>
            <div className="min-w-0">
              <p className="font-semibold">{draft.name || 'Service name'}</p>
              <p className="mt-0.5 text-sm text-[var(--ink-2)]">
                {draft.description || 'A short description for the browse page.'}
              </p>
            </div>
          </div>
        </div>

        <Field id="name" label="Name" hint="Shown on the card and in the companion picker.">
          <input
            id="name"
            className={inputClass}
            value={draft.name}
            maxLength={60}
            onChange={(e) => {
              const name = e.target.value;
              setDraft((d) => ({ ...d, name, slug: slugTouched ? d.slug : slugify(name) }));
            }}
          />
        </Field>

        {showSlug ? (
          <Field
            id="slug"
            label="URL name"
            prefix="/services/"
            hint="Permanent once created — it goes into every link to this service."
          >
            <input
              id="slug"
              className={inputClass}
              value={draft.slug}
              maxLength={40}
              onChange={(e) => {
                setSlugTouched(true);
                set('slug', e.target.value);
              }}
            />
          </Field>
        ) : (
          <div>
            <p className="mb-2 block text-sm font-medium">URL name</p>
            <p className="font-mono text-sm text-[var(--muted)]">/services/{draft.slug}</p>
            <p className="mt-1.5 text-xs text-[var(--muted)]">
              Cannot be changed — links to it already exist.
            </p>
          </div>
        )}

        <Field id="description" label="Description" hint="One or two lines, on the browse card.">
          <textarea
            id="description"
            className={inputClass}
            rows={2}
            maxLength={400}
            value={draft.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </Field>

        <Field
          id="guidance"
          label="Guidance for companions"
          hint="Shown when a companion adds this service. What is and is not in scope."
        >
          <textarea
            id="guidance"
            className={inputClass}
            rows={3}
            maxLength={2000}
            value={draft.guidance}
            onChange={(e) => set('guidance', e.target.value)}
          />
        </Field>

        <div>
          <p className="mb-2 block text-sm font-medium">Icon</p>
          <div className="flex flex-wrap gap-2">
            {ICON_KEYS.map((key) => {
              const on = draft.iconKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  title={key}
                  aria-label={key}
                  aria-pressed={on}
                  onClick={() => set('iconKey', key)}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border transition-all"
                  style={{
                    borderColor: on ? `hsl(${draft.hue} 58% 42%)` : 'var(--line)',
                    ...(on ? tint : { background: 'var(--surface)', color: 'var(--ink-2)' }),
                  }}
                >
                  <ServiceIcon iconKey={key} size={20} />
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[var(--muted)]">
            Line icons, not photos — they load instantly and stay sharp at any size. See
            docs/04-IMAGE-SPEC.md.
          </p>
        </div>

        <div>
          <label htmlFor="hue" className="mb-2 block text-sm font-medium">
            Accent colour
          </label>
          <input
            id="hue"
            type="range"
            min={0}
            max={359}
            value={draft.hue}
            onChange={(e) => set('hue', Number(e.target.value))}
            className="w-full"
            style={{ accentColor: `hsl(${draft.hue} 58% 42%)` }}
          />
          <p className="mt-1.5 text-xs text-[var(--muted)]">
            Keep it at least 20 degrees from the others, or two services look identical on the
            browse page.
          </p>
        </div>

        <div>
          <p className="mb-2 block text-sm font-medium">How it can be booked</p>
          <div className="flex flex-wrap gap-2">
            {BOOKING_TYPES.map((t) => {
              const on = draft.allowedBookingTypes.includes(t);
              const isLastOne = on && draft.allowedBookingTypes.length === 1;
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={on}
                  disabled={isLastOne}
                  title={isLastOne ? 'A service needs at least one way to book it.' : undefined}
                  onClick={() => toggleBookingType(t)}
                  className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition-all disabled:cursor-not-allowed"
                  style={{
                    borderColor: on ? 'var(--marigold)' : 'var(--line)',
                    background: on ? 'var(--marigold-soft)' : 'var(--surface)',
                    color: on ? 'var(--marigold)' : 'var(--ink-2)',
                  }}
                >
                  <BookingTypeIcon type={t} size={15} />
                  {bookingTypeLabel(t)}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-[var(--muted)]">
            Remote channels bill per minute, in person per hour. That is fixed by the channel, not
            chosen here.
          </p>
        </div>

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={draft.requiresBackgroundCheck}
            onChange={(e) => set('requiresBackgroundCheck', e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="text-sm font-medium">Requires a background check</span>
            <span className="mt-0.5 block text-xs text-[var(--muted)]">
              For services involving vulnerable people, alone and unsupervised. Recorded on the
              category; nothing enforces it yet.
            </span>
          </span>
        </label>

        <Field
          id="sortOrder"
          label="Sort order"
          hint="Lower numbers come first on the browse page."
        >
          <input
            id="sortOrder"
            type="number"
            min={0}
            max={999}
            className={inputClass}
            value={draft.sortOrder}
            onChange={(e) => set('sortOrder', Number(e.target.value))}
          />
        </Field>

        {error && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button onClick={submit} disabled={busy || !draft.name.trim() || !draft.slug.trim()}>
            {busy ? 'Saving…' : submitLabel}
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
